import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Gift, Sparkles, Loader2, CheckCircle2, XCircle, Radio } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * /redeem/:code — public preview + one-click redeem for promo codes.
 * Anonymous visitors see the offer and are pushed to signup with the code
 * preserved via localStorage → auto-redeems on first authenticated load.
 */
export default function Redeem() {
  const { code } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/promo/check/${encodeURIComponent(code)}`);
        if (alive) setInfo(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Invalid code");
      }
    })();
    return () => { alive = false; };
  }, [code]);

  // Auto-redeem the moment we know the user is logged in (from email flow)
  useEffect(() => {
    if (authLoading || !user || !info || result || redeeming || err) return;
    if (info.used) return; // don't try to redeem an already-used code
    (async () => {
      setRedeeming(true);
      try {
        const token = localStorage.getItem("fliboe_token");
        const { data } = await api.post(
          "/promo/redeem",
          { code },
          token ? { headers: { Authorization: `Bearer ${token}` } } : {}
        );
        setResult(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not redeem this code");
      } finally {
        setRedeeming(false);
      }
    })();
  }, [authLoading, user, info, code, result, redeeming, err]);

  const goSignup = () => {
    // Preserve the code so the signup flow can redirect back post-auth
    localStorage.setItem("fliboe_pending_promo", code);
    navigate(`/signup?next=/redeem/${encodeURIComponent(code)}`);
  };
  const goLogin = () => {
    localStorage.setItem("fliboe_pending_promo", code);
    navigate(`/login?next=/redeem/${encodeURIComponent(code)}`);
  };

  return (
    <main className="max-w-xl mx-auto px-5 py-16 sm:py-24" data-testid="redeem-page">
      <div className="fl-card p-8 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-200 text-xs font-semibold tracking-wide uppercase mb-6">
          <Gift className="w-3.5 h-3.5" /> Fliboe FM · Promo code
        </div>

        {err && !info && (
          <>
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h1 className="fl-display text-3xl font-bold text-white mb-2">Code not found</h1>
            <p className="text-white/60">{err}</p>
            <Link to="/" className="fl-btn mt-6 inline-flex" data-testid="redeem-back-home">Back to Fliboe</Link>
          </>
        )}

        {info && !result && (
          <>
            <h1 className="fl-display text-4xl font-black text-white mb-2">
              {info.months} month{info.months > 1 ? "s" : ""} of <span className="text-emerald-400">Premium</span> — free
            </h1>
            <p className="text-white/60 mb-6">
              This one-time code unlocks the paid Premium tier ($4.99/mo value) on your Fliboe account.
              No card required.
            </p>
            <div className="font-mono text-2xl tracking-[0.4em] text-emerald-300 bg-black/40 border border-emerald-500/20 rounded-xl py-4 mb-6" data-testid="redeem-code-display">
              {info.code}
            </div>

            {info.used ? (
              <div className="text-white/70 text-sm">This code has already been redeemed.</div>
            ) : authLoading ? (
              <div className="text-white/40 text-sm">Checking session…</div>
            ) : !user ? (
              <div className="flex flex-wrap justify-center gap-3">
                <button onClick={goSignup} className="fl-btn" data-testid="redeem-signup-btn">
                  <Sparkles className="w-4 h-4" /> Create account &amp; claim
                </button>
                <button onClick={goLogin} className="fl-btn ghost" data-testid="redeem-login-btn">
                  I already have an account
                </button>
              </div>
            ) : redeeming ? (
              <div className="inline-flex items-center gap-2 text-white/70"><Loader2 className="w-4 h-4 animate-spin" /> Applying to your account…</div>
            ) : null}
          </>
        )}

        {result && (
          <div data-testid="redeem-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
            <h1 className="fl-display text-3xl font-bold text-white mb-2">You&apos;re on Premium 🎉</h1>
            <p className="text-white/70">
              {result.months} month{result.months > 1 ? "s" : ""} of Premium is now active on <strong>{user?.email}</strong>.
            </p>
            <p className="text-white/50 text-sm mt-2">
              Active until {result.premium_until ? new Date(result.premium_until).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—"}.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard" className="fl-btn" data-testid="redeem-go-dashboard">
                <Radio className="w-4 h-4" /> Open your studio
              </Link>
              <Link to="/dashboard#referrals" className="fl-btn ghost" data-testid="redeem-view-referrals">
                <Sparkles className="w-4 h-4" /> Grab your referral link
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
